
import {
  atualizarPasso,
  excluirCampanha as excluirCampanhaRemota,
  listarCampanhas,
  salvarCampanha,
  syncToAll,
} from './dados-service';
import { buildRegionsFromPreset, TERRITORY_PRESETS, TerritoryPreset, MASTER_BRAZIL_CITIES } from './master-territory-data';
import { ibgeService } from './ibge-service';
import { NICHES } from './target-data';

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

function handleSupabaseError(error: any, operationType: OperationType, path: string | null) {
  const errInfo = {
    error: error?.message || String(error),
    operationType,
    path,
    authInfo: { userId: 'anonymous_or_managed_key' }
  };
  console.error('Supabase Error in CampaignService: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

export interface CampaignStep {
  targetId: string;
  targetName: string;
  regionId: string;
  regionName: string;
  cityName: string;
  status: 'pending' | 'completed' | 'processing' | 'skipped';
  leadsFound: number;
  lat?: number;
  lng?: number;
}

export interface DemographicFilter {
  type: string;
  values: string[];
}

export interface Campaign {
  id: string;
  name: string;
  status: 'active' | 'paused' | 'completed';
  createdAt: number;
  updatedAt: number;
  steps: CampaignStep[];
  currentStepIndex: number;
  totalLeads: number;
  scanModes?: ('standard' | 'exhaustive' | 'micro')[];
  autoEnrich?: boolean;
  stepDelay?: number;
  demographics?: DemographicFilter[];
}

class CampaignService {
  private campaigns: Campaign[] = [];
  private draft: { 
    targets: {id: string, name: string}[], 
    regions: {id: string, name: string, cityName: string, lat?: number, lng?: number}[],
    demographics: DemographicFilter[]
  } | null = null;
  public editingCampaignId: string | null = null;

  constructor() {
    this.loadFromStorage();
  }

  async migrate() {
    const saved = localStorage.getItem('google_maps_campaigns');
    if (!saved) {
      console.log('CampaignService: No local campaigns to migrate.');
      await this.initialSync();
      return;
    }

    try {
      const localCampaigns: Campaign[] = JSON.parse(saved);
      if (localCampaigns.length > 0) {
        console.log(`CampaignService: Found ${localCampaigns.length} campaigns in localStorage. Migrating to Supabase...`);
        
        for (const campaign of localCampaigns) {
          try {
            // Uma chamada só. O servidor faz upsert de campanha e passos na MESMA
            // transação, com único em (campanha, alvo, região) — então reenviar o
            // mesmo lote não duplica passo, e o "erro 23505 é ok" some do código.
            await syncToAll(() => salvarCampanha(campaign as any, false));
          } catch (err) {
            console.error(`CampaignService: Failed to migrate campaign ${campaign.id}`, err);
          }
        }
        console.log('CampaignService: Local campaigns migration attempt finished.');
      }
      
      // ONLY remove if we finished the loop without a crash
      localStorage.removeItem('google_maps_campaigns');
    } catch (e) {
      console.error('CampaignService: Migration parsing failed', e);
      // We DON'T remove if it failed to parse, user might still have it
    } finally {
      await this.initialSync();
    }
  }

  private async initialSync() {
    try {
      console.log('CampaignService: sincronizando com a API...');

      // O `select('*, steps:campaign_steps(*)')` com fallback por mensagem de
      // erro ("se a mensagem contiver 'column', tente de novo sem ordenar") era
      // um contorno para esquema que ninguém sabia qual era. Com o contrato, a
      // forma da resposta é conhecida em tempo de compilação.
      const remotas = await listarCampanhas();

      if (remotas.length > 0) {
        this.processRemoteCampaigns(remotas as any);
      } else {
        console.warn('CampaignService: nenhuma campanha no servidor.');
        window.dispatchEvent(new CustomEvent('campaigns-persisted'));
      }
    } catch (e) {
      console.error('CampaignService: Sync failed', e);
      window.dispatchEvent(new CustomEvent('campaigns-persisted'));
    }
  }

  private processRemoteCampaigns(data: any[]) {
    const remoteCampaigns = data.map((c: any) => ({
      ...c,
      id: String(c.id),
      name: String(c.name || 'Sem nome'),
      status: c.status || 'paused',
      createdAt: new Date(c.createdAt || c.created_at || Date.now()).getTime(),
      updatedAt: new Date(c.updatedAt || c.updated_at || Date.now()).getTime(),
      autoEnrich: !!(c.auto_enrich || c.autoEnrich),
      totalLeads: Number(c.total_leads || c.totalLeads || 0),
      currentStepIndex: Number(c.current_step_index || c.currentStepIndex || 0),
      steps: (c.steps || []).map((s: any) => ({
        targetId: String(s.target_id || s.targetId),
        targetName: String(s.target_name || s.targetName),
        regionId: String(s.region_id || s.regionId),
        regionName: String(s.region_name || s.regionName),
        cityName: String(s.city_name || s.cityName),
        status: s.status || 'pending',
        leadsFound: Number(s.leads_found || s.leadsFound || 0),
        lat: s.lat,
        lng: s.lng
      }))
    })) as Campaign[];

    let hasChanges = false;
    const localMap = new Map(this.campaigns.map(c => [c.id, c]));

    remoteCampaigns.forEach(rc => {
      const local = localMap.get(rc.id);
      if (!local) {
        this.campaigns.push(rc);
        hasChanges = true;
      } else {
        // Merge simples: Cloud manda nos dados se forem mais recentes
        // Ou simplesmente atualiza se o cloud tiver algo diferente
        if (JSON.stringify(local) !== JSON.stringify(rc)) {
          Object.assign(local, rc);
          hasChanges = true;
        }
      }
    });
    
    // Sempre salva e notifica se houve merge ou se é o boot inicial
    this.saveToStorage();
    window.dispatchEvent(new CustomEvent('campaigns-persisted'));
    console.log(`CampaignService: Processed ${remoteCampaigns.length} campaigns from cloud.`);
  }

  getDraft() {
    return this.draft;
  }

  startDraft() {
    this.draft = { targets: [], regions: [], demographics: [] };
    this.editingCampaignId = null;
  }

  cancelDraft() {
    this.draft = null;
    this.editingCampaignId = null;
  }

  loadTerritoryTemplateIntoDraft(presetId: string, autoExpandKnownNeighborhoods: boolean = true) {
    const regions = buildRegionsFromPreset(presetId, autoExpandKnownNeighborhoods);
    this.draft = {
      targets: [], // Alvos limpos para o usuário escolher livremente
      regions,
      demographics: []
    };
    this.editingCampaignId = null;
    return regions;
  }

  loadCampaignIntoDraft(campaignId: string): boolean {
    const campaign = this.getCampaign(campaignId);
    if (!campaign) return false;

    const targetsMap = new Map<string, { id: string; name: string }>();
    const regionsMap = new Map<string, { id: string; name: string; cityName: string; lat?: number; lng?: number }>();

    campaign.steps.forEach(s => {
      if (!targetsMap.has(s.targetId)) {
        targetsMap.set(s.targetId, { id: s.targetId, name: s.targetName });
      }
      if (!regionsMap.has(s.regionId)) {
        regionsMap.set(s.regionId, {
          id: s.regionId,
          name: s.regionName,
          cityName: s.cityName,
          lat: s.lat,
          lng: s.lng
        });
      }
    });

    this.draft = {
      targets: Array.from(targetsMap.values()),
      regions: Array.from(regionsMap.values()),
      demographics: campaign.demographics ? [...campaign.demographics] : []
    };
    this.editingCampaignId = campaignId;
    return true;
  }

  toggleTargetInDraft(target: {id: string, name: string}) {
    if (!this.draft) this.startDraft();
    if (!this.draft) return;
    const idx = this.draft.targets.findIndex(t => t.id === target.id);
    if (idx >= 0) {
      this.draft.targets = this.draft.targets.filter(t => t.id !== target.id);
    } else {
      this.draft.targets.push(target);
    }
  }

  addTargetsToDraft(targets: {id: string, name: string}[]) {
    if (!this.draft) return;
    targets.forEach(t => {
      if (!this.draft!.targets.some(existing => existing.id === t.id)) {
        this.draft!.targets.push(t);
      }
    });
  }

  removeTargetsFromDraft(targetIds: string[]) {
    if (!this.draft) return;
    this.draft.targets = this.draft.targets.filter(t => !targetIds.includes(t.id));
  }

  addRegionsToDraft(regions: {id: string, name: string, cityName: string, lat?: number, lng?: number}[]) {
    if (!this.draft) this.startDraft();
    if (!this.draft) return;
    regions.forEach(r => {
      if (!this.draft!.regions.some(existing => existing.id === r.id)) {
        this.draft!.regions.push(r);
      }
    });
  }

  removeRegionsFromDraft(regionIds: string[]) {
    if (!this.draft) return;
    const set = new Set(regionIds);
    this.draft.regions = this.draft.regions.filter(r => !set.has(r.id));
  }

  toggleRegionInDraft(region: {id: string, name: string, cityName: string, lat?: number, lng?: number}) {
    if (!this.draft) this.startDraft();
    if (!this.draft) return;
    const idx = this.draft.regions.findIndex(r => r.id === region.id);
    if (idx >= 0) {
      this.draft.regions = this.draft.regions.filter(r => r.id !== region.id);
    } else {
      this.draft.regions.push(region);
    }
  }

  updateDemographicsInDraft(filter: DemographicFilter) {
    if (!this.draft) return;
    const idx = this.draft.demographics.findIndex(d => d.type === filter.type);
    if (idx >= 0) {
      if (filter.values.length === 0) {
        this.draft.demographics = this.draft.demographics.filter(d => d.type !== filter.type);
      } else {
        this.draft.demographics[idx] = filter;
      }
    } else if (filter.values.length > 0) {
      this.draft.demographics.push(filter);
    }
  }

  private loadFromStorage() {
    const saved = localStorage.getItem('google_maps_campaigns');
    if (saved) {
      try {
        this.campaigns = JSON.parse(saved);
      } catch (e) {
        this.campaigns = [];
      }
    }
  }

  private saveToStorage() {
    localStorage.setItem('google_maps_campaigns', JSON.stringify(this.campaigns));
  }

  getCampaigns() {
    return this.campaigns;
  }

  getCampaign(id: string) {
    return this.campaigns.find(c => c.id === id);
  }

  async createCampaign(name: string, targets: {id: string, name: string}[], regions: {id: string, name: string, cityName: string, lat?: number, lng?: number}[], demographics?: DemographicFilter[]) {
    const steps: CampaignStep[] = [];
    
    // Lógica inteligente de cruzamento: Para cada região, roda todos os alvos
    regions.forEach(region => {
      targets.forEach(target => {
        steps.push({
          targetId: target.id,
          targetName: target.name,
          regionId: region.id,
          regionName: region.name,
          cityName: region.cityName,
          lat: region.lat,
          lng: region.lng,
          status: 'pending',
          leadsFound: 0
        });
      });
    });

    const newCampaign: Campaign = {
      id: Math.random().toString(36).substr(2, 9),
      name,
      status: 'paused',
      createdAt: Date.now(),
      updatedAt: Date.now(),
      steps,
      currentStepIndex: 0,
      totalLeads: 0,
      scanModes: ['standard'],
      autoEnrich: false,
      stepDelay: 2000,
      demographics: demographics || []
    };

    // Sync to Supabase PRIMEIRO
    try {
      await syncToAll(async (client) => {
        const { error: cError } = await client.from('campaigns').insert({
          id: newCampaign.id,
          name: newCampaign.name,
          status: newCampaign.status,
          createdAt: new Date(newCampaign.createdAt).toISOString(),
          updatedAt: new Date(newCampaign.updatedAt).toISOString(),
          current_step_index: newCampaign.currentStepIndex,
          total_leads: newCampaign.totalLeads,
          auto_enrich: newCampaign.autoEnrich
        });

        if (cError) {
          handleSupabaseError(cError, OperationType.CREATE, 'campaigns');
        }

        const allSteps = newCampaign.steps.map(s => ({
          campaign_id: newCampaign.id,
          target_id: s.targetId,
          target_name: s.targetName,
          region_id: s.regionId,
          region_name: s.regionName,
          city_name: s.cityName,
          status: s.status,
          leads_found: s.leadsFound,
          lat: s.lat,
          lng: s.lng
        }));

        const chunkSize = 200;
        for (let i = 0; i < allSteps.length; i += chunkSize) {
          const chunk = allSteps.slice(i, i + chunkSize);
          const { error: sError } = await client.from('campaign_steps').insert(chunk);
          if (sError) {
            handleSupabaseError(sError, OperationType.CREATE, 'campaign_steps');
          }
        }
      });
    } catch (e) {
      console.error('CampaignService: Cloud creation failed', e);
    }

    this.campaigns.push(newCampaign);
    this.saveToStorage();
    window.dispatchEvent(new CustomEvent('campaigns-persisted'));

    return newCampaign;
  }

  async updateCampaignStatus(id: string, status: 'active' | 'paused' | 'completed') {
    const campaign = this.getCampaign(id);
    if (campaign) {
      // Deactivate others if this one is becoming active
      if (status === 'active') {
        this.campaigns.forEach(c => {
          if (c.id !== id && c.status === 'active') c.status = 'paused';
        });
      }
      campaign.status = status;
      campaign.updatedAt = Date.now();
      this.saveToStorage();

      // Sync to Supabase PRIMEIRO
      try {
        await syncToAll(async (client) => {
          const { error } = await client.from('campaigns').update({ 
            status, 
            updatedAt: new Date(campaign.updatedAt).toISOString() 
          }).eq('id', id);

          if (error) {
            handleSupabaseError(error, OperationType.UPDATE, 'campaigns');
          }
        });
      } catch (e) {
        console.error('CampaignService: Failed to sync status to cloud', e);
      }
    }
  }

  getActiveCampaign() {
    return this.campaigns.find(c => c.status === 'active');
  }

  checkStepExists(targetId: string, regionId: string) {
    return this.campaigns.some(c => 
      c.steps.some(s => s.targetId === targetId && s.regionId === regionId && (s.status === 'completed' || s.status === 'processing'))
    );
  }

  getScannedCountForRegion(regionId: string) {
    const uniqueTargets = new Set();
    this.campaigns.forEach(c => {
      c.steps.forEach(s => {
        if (s.regionId === regionId && (s.status === 'completed' || s.status === 'processing')) {
          uniqueTargets.add(s.targetId);
        }
      });
    });
    return uniqueTargets.size;
  }

  getRegionsScannedForTarget(targetId: string) {
    const uniqueRegions = new Set<string>();
    this.campaigns.forEach(c => {
      c.steps.forEach(s => {
        if (s.targetId === targetId && (s.status === 'completed' || s.status === 'processing')) {
          uniqueRegions.add(s.regionName);
        }
      });
    });
    return Array.from(uniqueRegions);
  }

  getCurrentStep() {
    const active = this.getActiveCampaign();
    if (!active) return null;
    if (active.currentStepIndex >= active.steps.length) return null;
    return {
      campaign: active,
      step: active.steps[active.currentStepIndex]
    };
  }

  async optimizeCampaign(campaignId: string) {
    const campaign = this.campaigns.find(c => c.id === campaignId);
    if (!campaign) return 0;

    let skipCount = 0;
    const stepsToUpdate = [];

    for (let i = 0; i < campaign.steps.length; i++) {
       const step = campaign.steps[i];
       // Só tentamos pular o que ainda não foi feito ou pulado
       if (step.status === 'pending') {
          // Verifica se JÁ EXISTE em OUTRA campanha (não na mesma)
          const existsElsewhere = this.campaigns.some(c => 
            c.id !== campaignId && 
            c.steps.some(s => s.targetId === step.targetId && s.regionId === step.regionId && (s.status === 'completed' || s.status === 'processing'))
          );

          if (existsElsewhere) {
             step.status = 'skipped';
             step.leadsFound = 0;
             skipCount++;
             stepsToUpdate.push(step);
          }
       }
    }

    if (skipCount > 0) {
      this.saveToStorage();
      try {
        // Atualiza no Cloud coletivamente (simplificado)
        for (const step of stepsToUpdate) {
           await syncToAll(async (client) => {
             await client.from('campaign_steps')
               .update({ status: 'skipped', completed_at: new Date().toISOString() })
               .match({ campaign_id: campaignId, target_id: step.targetId, region_id: step.regionId });
           });
        }
      } catch (e) {
        console.error('CampaignService: Error syncing optimization to cloud', e);
      }
    }
    
    return skipCount;
  }

  /**
   * Sincroniza e expande uma campanha existente com os novos bairros e alvos atualizados no projeto.
   * Não duplica tarefas existentes e preserva 100% do progresso e leads capturados.
   */
  async syncAndExpandCampaign(campaignId: string): Promise<{ addedStepsCount: number; newTotalSteps: number; message: string }> {
    const campaign = this.campaigns.find(c => c.id === campaignId);
    if (!campaign) {
      return { addedStepsCount: 0, newTotalSteps: 0, message: 'Campanha não encontrada.' };
    }

    // 1. Extrair os alvos atuais da campanha
    const targetMap = new Map<string, { id: string; name: string }>();
    campaign.steps.forEach(s => {
      if (!targetMap.has(s.targetId)) {
        targetMap.set(s.targetId, { id: s.targetId, name: s.targetName });
      }
    });

    // Se a campanha não tiver alvos identificáveis nas steps (raro), buscar no catálogo
    if (targetMap.size === 0) {
      NICHES.forEach(n => {
        n.segments.forEach(seg => {
          seg.targets.forEach(t => {
            targetMap.set(t.id, { id: t.id, name: t.name });
          });
        });
      });
    }

    const currentTargets = Array.from(targetMap.values());

    // 2. Extrair as cidades presentes na campanha
    const detectedCities = new Map<string, { cityName: string; uf: string; lat?: number; lng?: number }>();
    
    campaign.steps.forEach(s => {
      const cityKey = s.cityName || s.regionName;
      if (cityKey && !detectedCities.has(cityKey)) {
        // Tenta separar "São Paulo - SP" ou "Rio de Janeiro - RJ"
        let rawCity = cityKey;
        let uf = '';
        if (rawCity.includes(' - ')) {
          const parts = rawCity.split(' - ');
          rawCity = parts[0].trim();
          uf = parts[1]?.trim() || '';
        } else if (rawCity.includes('/')) {
          const parts = rawCity.split('/');
          rawCity = parts[0].trim();
          uf = parts[1]?.trim() || '';
        }

        // Tenta achar na base mestre para coordenadas
        const matched = MASTER_BRAZIL_CITIES.find(mc => 
          mc.nome.toLowerCase() === rawCity.toLowerCase() && (!uf || mc.uf.toUpperCase() === uf.toUpperCase())
        );

        detectedCities.set(cityKey, {
          cityName: cityKey,
          uf: uf || (matched ? matched.uf : ''),
          lat: s.lat || (matched ? matched.lat : undefined),
          lng: s.lng || (matched ? matched.lng : undefined)
        });
      }
    });

    // 3. Gerar a lista atualizada e expandida de regiões para todas as cidades da campanha
    const expandedRegions: { id: string; name: string; cityName: string; lat?: number; lng?: number }[] = [];

    detectedCities.forEach((cityInfo, fullCityName) => {
      let rawCity = fullCityName;
      let uf = cityInfo.uf;
      if (rawCity.includes(' - ')) {
        const parts = rawCity.split(' - ');
        rawCity = parts[0].trim();
        uf = parts[1]?.trim() || uf;
      }

      // Verifica se a cidade possui bairros curados no ibgeService
      const knownNhs = ibgeService.getKnownNeighborhoodsForCity(rawCity, uf);
      if (knownNhs && knownNhs.length > 0) {
        knownNhs.forEach((nh, idx) => {
          expandedRegions.push({
            id: `reg_${uf || 'uf'}_${rawCity.replace(/\s+/g, '_')}_${idx}_${nh.nome.replace(/\s+/g, '_')}`.toLowerCase(),
            name: nh.nome,
            cityName: fullCityName,
            lat: nh.lat || cityInfo.lat,
            lng: nh.lng || cityInfo.lng
          });
        });
      } else {
        // Mantém a cidade padrão
        expandedRegions.push({
          id: `city_${uf || 'uf'}_${rawCity.replace(/\s+/g, '_')}`.toLowerCase(),
          name: fullCityName,
          cityName: fullCityName,
          lat: cityInfo.lat,
          lng: cityInfo.lng
        });
      }
    });

    // 4. Mapear o que já existe nesta campanha para não duplicar nada
    // Chave única: targetId + regionName (ou targetId + regionId)
    const existingStepKeys = new Set<string>();
    campaign.steps.forEach(s => {
      existingStepKeys.add(`${s.targetId}:::${s.regionName.toLowerCase()}:::${s.cityName.toLowerCase()}`);
      existingStepKeys.add(`${s.targetId}:::${s.regionId}`);
    });

    // 5. Cruzar as regiões expandidas com todos os alvos e coletar apenas as etapas INÉDITAS
    const newStepsToAdd: CampaignStep[] = [];

    expandedRegions.forEach(region => {
      currentTargets.forEach(target => {
        const key1 = `${target.id}:::${region.name.toLowerCase()}:::${region.cityName.toLowerCase()}`;
        const key2 = `${target.id}:::${region.id}`;

        if (!existingStepKeys.has(key1) && !existingStepKeys.has(key2)) {
          newStepsToAdd.push({
            targetId: target.id,
            targetName: target.name,
            regionId: region.id,
            regionName: region.name,
            cityName: region.cityName,
            lat: region.lat,
            lng: region.lng,
            status: 'pending',
            leadsFound: 0
          });
          // Marca para evitar duplicação interna
          existingStepKeys.add(key1);
          existingStepKeys.add(key2);
        }
      });
    });

    if (newStepsToAdd.length === 0) {
      return {
        addedStepsCount: 0,
        newTotalSteps: campaign.steps.length,
        message: 'A campanha já está 100% atualizada com todos os bairros e termos mais recentes do sistema!'
      };
    }

    // 6. Adicionar as novas etapas ao final da campanha
    campaign.steps.push(...newStepsToAdd);
    campaign.updatedAt = Date.now();

    // Se a campanha estava 'completed', mas agora ganhou novos passos, reativa para 'paused' ou 'active'
    if (campaign.status === 'completed' && campaign.currentStepIndex < campaign.steps.length) {
      campaign.status = 'paused';
    }

    this.saveToStorage();

    // 7. Persistir no Supabase em lotes
    try {
      await syncToAll(async (client) => {
        await client.from('campaigns').update({
          status: campaign.status,
          updatedAt: new Date(campaign.updatedAt).toISOString()
        }).eq('id', campaign.id);

        const allNewSteps = newStepsToAdd.map(s => ({
          campaign_id: campaign.id,
          target_id: s.targetId,
          target_name: s.targetName,
          region_id: s.regionId,
          region_name: s.regionName,
          city_name: s.cityName,
          status: s.status,
          leads_found: s.leadsFound,
          lat: s.lat,
          lng: s.lng
        }));

        const chunkSize = 200;
        for (let i = 0; i < allNewSteps.length; i += chunkSize) {
          const chunk = allNewSteps.slice(i, i + chunkSize);
          await client.from('campaign_steps').insert(chunk);
        }
      });
    } catch (e) {
      console.error('CampaignService: Failed to sync expanded steps to cloud', e);
    }

    window.dispatchEvent(new CustomEvent('campaigns-persisted'));

    return {
      addedStepsCount: newStepsToAdd.length,
      newTotalSteps: campaign.steps.length,
      message: `🎉 Campanha expandida com sucesso! ${newStepsToAdd.length} novas etapas de bairros e regiões foram adicionadas.`
    };
  }

  async skipStep() {
    const active = this.getActiveCampaign();
    if (!active) return;

    const step = active.steps[active.currentStepIndex];
    if (step) {
      step.status = 'skipped';
      step.leadsFound = 0;
      active.currentStepIndex++;
      
      if (active.currentStepIndex >= active.steps.length) {
        active.status = 'completed';
      }
      
      active.updatedAt = Date.now();
      this.saveToStorage();

      try {
        await syncToAll(async (client) => {
          await client.from('campaigns').update({
            current_step_index: active.currentStepIndex,
            status: active.status,
            updatedAt: new Date(active.updatedAt).toISOString()
          }).eq('id', active.id);

          await client.from('campaign_steps')
            .update({ status: 'skipped', completed_at: new Date().toISOString() })
            .match({ campaign_id: active.id, target_id: step.targetId, region_id: step.regionId });
        });
      } catch (e) {
        console.error('CampaignService: Failed to skip step in cloud', e);
      }
    }
  }

  async completeStep(leadsCount: number) {
    const active = this.getActiveCampaign();
    if (!active) return;

    const step = active.steps[active.currentStepIndex];
    if (step) {
      step.status = 'completed';
      step.leadsFound = leadsCount;
      active.totalLeads += leadsCount;
      active.currentStepIndex++;
      
      if (active.currentStepIndex >= active.steps.length) {
        active.status = 'completed';
      }
      
      active.updatedAt = Date.now();
      this.saveToStorage();

      // Sync to Supabase PRIMEIRO
      try {
        await syncToAll(async (client) => {
          const { error: cError } = await client.from('campaigns').update({
            current_step_index: active.currentStepIndex,
            total_leads: active.totalLeads,
            status: active.status,
            updatedAt: new Date(active.updatedAt).toISOString()
          }).eq('id', active.id);

          if (cError) {
            handleSupabaseError(cError, OperationType.UPDATE, 'campaigns');
          }

          const { error: sError } = await client.from('campaign_steps')
            .update({ status: 'completed', leads_found: leadsCount, completed_at: new Date().toISOString() })
            .match({ campaign_id: active.id, target_id: step.targetId, region_id: step.regionId });

          if (sError) {
            handleSupabaseError(sError, OperationType.UPDATE, 'campaign_steps');
          }
        });
      } catch (e) {
        console.error('CampaignService: Failed to complete step in cloud', e);
      }
    }
  }

  async deleteCampaign(id: string) {
    this.campaigns = this.campaigns.filter(c => c.id !== id);
    this.saveToStorage();
    try {
      await syncToAll(async (client) => {
        await client.from('campaigns').delete().eq('id', id);
      });
    } catch (e) {
      console.error('CampaignService: Failed to delete from cloud', e);
    }
  }

  async updateCampaignWithSteps(
    id: string,
    updates: {
      name?: string;
      action?: 'update' | 'merge' | 'replace';
      newTargets?: { id: string; name: string }[];
      removeTargetIds?: string[];
      newRegions?: { id: string; name: string; cityName: string; lat?: number; lng?: number }[];
      removeRegionIds?: string[];
      autoEnrich?: boolean;
      stepDelay?: number;
      scanModes?: ('standard' | 'exhaustive' | 'micro')[];
      demographics?: DemographicFilter[];
    }
  ) {
    const campaign = this.getCampaign(id);
    if (!campaign) return null;

    if (updates.name) campaign.name = updates.name;
    if (updates.autoEnrich !== undefined) campaign.autoEnrich = updates.autoEnrich;
    if (updates.stepDelay !== undefined) campaign.stepDelay = updates.stepDelay;
    if (updates.scanModes) campaign.scanModes = updates.scanModes;
    if (updates.demographics) campaign.demographics = updates.demographics;

    const action = updates.action || 'merge';

    // 1. Mapeia alvos e regiões que já existem na campanha
    const existingTargetsMap = new Map<string, { id: string; name: string }>();
    const existingRegionsMap = new Map<string, { id: string; name: string; cityName: string; lat?: number; lng?: number }>();

    campaign.steps.forEach(s => {
      if (!existingTargetsMap.has(s.targetId)) {
        existingTargetsMap.set(s.targetId, { id: s.targetId, name: s.targetName });
      }
      if (!existingRegionsMap.has(s.regionId)) {
        existingRegionsMap.set(s.regionId, {
          id: s.regionId,
          name: s.regionName,
          cityName: s.cityName,
          lat: s.lat,
          lng: s.lng
        });
      }
    });

    // 2. Mescla novos alvos e novas regiões recebidas via JSON
    if (updates.newTargets && updates.newTargets.length > 0) {
      updates.newTargets.forEach(t => {
        if (!existingTargetsMap.has(t.id)) {
          existingTargetsMap.set(t.id, t);
        }
      });
    }

    if (updates.newRegions && updates.newRegions.length > 0) {
      updates.newRegions.forEach(r => {
        if (!existingRegionsMap.has(r.id)) {
          existingRegionsMap.set(r.id, r);
        }
      });
    }

    // 3. Aplica remoções solicitadas
    if (updates.removeTargetIds && updates.removeTargetIds.length > 0) {
      updates.removeTargetIds.forEach(id => existingTargetsMap.delete(id));
      campaign.steps = campaign.steps.filter(s => s.status === 'completed' || !updates.removeTargetIds!.includes(s.targetId));
    }
    if (updates.removeRegionIds && updates.removeRegionIds.length > 0) {
      updates.removeRegionIds.forEach(id => existingRegionsMap.delete(id));
      campaign.steps = campaign.steps.filter(s => s.status === 'completed' || !updates.removeRegionIds!.includes(s.regionId));
    }

    const allTargets = Array.from(existingTargetsMap.values());
    const allRegions = Array.from(existingRegionsMap.values());

    if (action === 'replace' && updates.newTargets && updates.newRegions) {
      const steps: CampaignStep[] = [];
      updates.newRegions.forEach(region => {
        updates.newTargets!.forEach(target => {
          steps.push({
            targetId: target.id,
            targetName: target.name,
            regionId: region.id,
            regionName: region.name,
            cityName: region.cityName,
            lat: region.lat,
            lng: region.lng,
            status: 'pending',
            leadsFound: 0
          });
        });
      });
      campaign.steps = steps;
      campaign.currentStepIndex = 0;
    } else {
      // 4. Cruzamento Combinatório Inteligente: (Alvos Existentes + Novos) x (Regiões Existentes + Novas)
      allRegions.forEach(region => {
        allTargets.forEach(target => {
          const exists = campaign.steps.some(s => s.targetId === target.id && s.regionId === region.id);
          if (!exists) {
            campaign.steps.push({
              targetId: target.id,
              targetName: target.name,
              regionId: region.id,
              regionName: region.name,
              cityName: region.cityName,
              lat: region.lat,
              lng: region.lng,
              status: 'pending',
              leadsFound: 0
            });
          }
        });
      });
    }

    campaign.updatedAt = Date.now();
    this.saveToStorage();

    try {
      await syncToAll(async (client) => {
        await client.from('campaigns').update({
          name: campaign.name,
          auto_enrich: campaign.autoEnrich,
          updatedAt: new Date(campaign.updatedAt).toISOString()
        }).eq('id', campaign.id);

        if (campaign.steps.length > 0) {
          await client.from('campaign_steps').delete().eq('campaign_id', campaign.id);
          await client.from('campaign_steps').insert(campaign.steps.map(s => ({
            campaign_id: campaign.id,
            target_id: s.targetId,
            target_name: s.targetName,
            region_id: s.regionId,
            region_name: s.regionName,
            city_name: s.cityName,
            status: s.status,
            leads_found: s.leadsFound,
            lat: s.lat,
            lng: s.lng
          })));
        }
      });
    } catch (e) {
      console.error('CampaignService: Failed to sync campaign update to cloud', e);
    }

    window.dispatchEvent(new CustomEvent('campaigns-persisted'));
    return campaign;
  }

  /**
   * Atualiza as etapas de uma campanha substituindo cidades que foram expandidas em bairros
   */
  async replaceCityWithNeighborhoodsInCampaign(
    campaignId: string, 
    cityName: string, 
    neighborhoods: { id: string | number; nome: string; lat?: number; lng?: number }[]
  ) {
    const campaign = this.getCampaign(campaignId);
    if (!campaign || neighborhoods.length === 0) return;

    // Obtém alvos únicos da campanha
    const targetMap = new Map<string, string>();
    campaign.steps.forEach(s => {
      targetMap.set(s.targetId, s.targetName);
    });

    // Remove passos genéricos que eram da cidade inteira
    const otherSteps = campaign.steps.filter(s => !(s.cityName === cityName && s.regionName === cityName));

    // Para cada alvo e para cada novo bairro, adiciona o novo step se não existir
    neighborhoods.forEach(nh => {
      targetMap.forEach((targetName, targetId) => {
        const exists = otherSteps.some(s => s.targetId === targetId && s.cityName === cityName && s.regionName === nh.nome);
        if (!exists) {
          otherSteps.push({
            targetId,
            targetName,
            regionId: String(nh.id),
            regionName: nh.nome,
            cityName,
            lat: nh.lat,
            lng: nh.lng,
            status: 'pending',
            leadsFound: 0
          });
        }
      });
    });

    campaign.steps = otherSteps;
    campaign.updatedAt = Date.now();
    this.saveToStorage();

    try {
      await syncToAll(async (client) => {
        await client.from('campaign_steps').delete().eq('campaign_id', campaign.id);
        await client.from('campaign_steps').insert(campaign.steps.map(s => ({
          campaign_id: campaign.id,
          target_id: s.targetId,
          target_name: s.targetName,
          region_id: s.regionId,
          region_name: s.regionName,
          city_name: s.cityName,
          status: s.status,
          leads_found: s.leadsFound,
          lat: s.lat,
          lng: s.lng
        })));
      });
    } catch (e) {
      console.error('CampaignService: Failed to sync replaced neighborhoods', e);
    }

    window.dispatchEvent(new CustomEvent('campaigns-persisted'));
  }

  updateCampaignSettings(id: string, updates: Partial<{
    name: string, 
    scanModes: ('standard' | 'exhaustive' | 'micro')[],
    autoEnrich: boolean,
    stepDelay: number
  }>) {
    const campaign = this.getCampaign(id);
    if (campaign) {
      if (updates.name !== undefined) campaign.name = updates.name;
      if (updates.scanModes !== undefined) campaign.scanModes = updates.scanModes;
      if (updates.autoEnrich !== undefined) campaign.autoEnrich = updates.autoEnrich;
      if (updates.stepDelay !== undefined) campaign.stepDelay = updates.stepDelay;
      campaign.updatedAt = Date.now();
      this.saveToStorage();
    }
  }

  /**
   * Criação Rápida de Campanha a partir de um Template Territorial Mestre (1-Clique)
   */
  async createCampaignFromTerritoryTemplate(
    presetId: string,
    campaignName: string,
    targets: { id: string; name: string }[],
    options?: {
      scanModes?: ('standard' | 'exhaustive' | 'micro')[];
      autoEnrich?: boolean;
      stepDelay?: number;
      demographics?: DemographicFilter[];
      autoExpandKnownNeighborhoods?: boolean;
    }
  ): Promise<Campaign> {
    const regions = buildRegionsFromPreset(
      presetId, 
      options?.autoExpandKnownNeighborhoods !== false
    );

    const name = campaignName.trim() || `Campanha ${presetId.toUpperCase()} - ${targets[0]?.name || 'Alvos'}`;
    const campaign = await this.createCampaign(name, targets, regions, options?.demographics);

    if (options?.scanModes) campaign.scanModes = options.scanModes;
    if (options?.autoEnrich !== undefined) campaign.autoEnrich = options.autoEnrich;
    if (options?.stepDelay !== undefined) campaign.stepDelay = options.stepDelay;

    this.saveToStorage();
    window.dispatchEvent(new CustomEvent('campaigns-persisted'));
    return campaign;
  }

  /**
   * Adiciona Novos Alvos a uma Campanha Existente sem reprocessar etapas concluídas
   */
  async addTargetsToCampaign(campaignId: string, newTargets: { id: string; name: string }[]) {
    return this.updateCampaignWithSteps(campaignId, {
      newTargets,
      action: 'merge'
    });
  }

  /**
   * Adiciona Novas Regiões a uma Campanha Existente sem reprocessar etapas concluídas
   */
  async addRegionsToCampaign(
    campaignId: string, 
    newRegions: { id: string; name: string; cityName: string; lat?: number; lng?: number }[]
  ) {
    return this.updateCampaignWithSteps(campaignId, {
      newRegions,
      action: 'merge'
    });
  }

  /**
   * Edição Completa da Campanha com Reconciliação Inteligente de Etapas
   */
  async editCampaign(
    campaignId: string,
    updates: {
      name?: string;
      scanModes?: ('standard' | 'exhaustive' | 'micro')[];
      autoEnrich?: boolean;
      stepDelay?: number;
      newTargets?: { id: string; name: string }[];
      removeTargetIds?: string[];
      newRegions?: { id: string; name: string; cityName: string; lat?: number; lng?: number }[];
      removeRegionIds?: string[];
    }
  ) {
    return this.updateCampaignWithSteps(campaignId, updates);
  }
}

export const campaignService = new CampaignService();

